import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrentUserId, getDealApplicationId, setApplicationRoutingData, setEmailAddress, setPreferredLanguage } from "features/authSlice";
import { MyLoanInfo, useGetMyLoansQuery, useSendUserInteractedMutation } from "pages/application/applicationService";
import { ConsentTypes, useSetConsentMutation } from "pages/authentication/userService";
import { useGetBorrowerDetailsQuery } from "pages/dashboard/home/dashboardService";
import { normalizeToAppLanguage } from "utilities/getPreferredLanguage";
import LoansListV2 from "./loans-list/LoansListV2";

const pathsToShowMyLoans = ["/application", "/dashboard"];

export default function MyLoansV2() {
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const { data, isLoading, isFetching } = useGetMyLoansQuery(userId, { skip: !userId });
  const [setConsents] = useSetConsentMutation();
  const [sendUserInteracted] = useSendUserInteractedMutation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: userProfile } = useGetBorrowerDetailsQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!userProfile?.emailAddress) {
      return;
    }
    dispatch(setEmailAddress(userProfile.emailAddress));
  }, [userProfile?.emailAddress, dispatch]);

  useEffect(() => {
    if (dealApplicationId) {
      return;
    }

    if (!userProfile?.preferredLanguage) {
      return;
    }

    const resolved = normalizeToAppLanguage(userProfile.preferredLanguage);
    dispatch(setPreferredLanguage(resolved));
  }, [userProfile?.preferredLanguage, dealApplicationId, dispatch]);

  const navigateToPostAuth = useCallback(
    (index: number, loans: MyLoanInfo[]) => {
      const {
        nhApplicationId,
        nhApplicationStatus,
        nhDealApplicationId,
        applicationRoleType,
        dealApplicationId,
        loanId,
        hasPal,
        prospectId,
        loanPurpose,
        leadProvider,
        isConsentCollected,
        userRole,
        inquiryId,
        coBorrowerHasOwnNhApplication,
        nhApplicationPath,
        loanNumber,
        applicationNumber,
        userType,
        encompassLoanId,
        dealId,
        dealBorrowerId
      } = loans[index];

      void sendUserInteracted({
        applicationId: dealApplicationId ?? "",
        prospectId: prospectId ?? "",
        userType,
      });

      dispatch(
        setApplicationRoutingData({
          nhApplicationId,
          nhApplicationStatus,
          nhDealApplicationId,
          applicationRoleType,
          dealApplicationId,
          loanId,
          hasPal,
          prospectId,
          loanPurpose,
          leadProvider,
          isConsentCollected,
          userRole,
          inquiryId,
          coBorrowerHasOwnNhApplication,
          nhApplicationPath,
          loanNumber,
          applicationNumber,
          encompassLoanId,
          dealId,
          dealBorrowerId
        }),
      );

      navigate("/post-auth", { replace: true });
    },
    [dispatch, navigate, sendUserInteracted],
  );

  useEffect(() => {
    if (isLoading || !data) {
      return;
    }
    data.forEach((myLoan) => {
      void setConsents({
        id: myLoan.nhDealApplicationId,
        consent: {
          userId,
          nhDealApplicationId: myLoan.nhDealApplicationId,
          prospectId: myLoan.prospectId,
          consentTypes: [ConsentTypes.PrivacyPolicy, ConsentTypes.TermsOfUse],
        },
      });
    });

    if (data.length === 1) {
      const previousPath =
        location.state != null && typeof location.state === "object" && "previousPath" in location.state
          ? (location.state as { previousPath: unknown }).previousPath
          : null;

      if (!pathsToShowMyLoans.some((path) => typeof previousPath === "string" && previousPath.includes(path))) {
        navigateToPostAuth(0, data);
      }

      return;
    }

  }, [data, isLoading, location.state, navigateToPostAuth, setConsents, userId]);

  const handleLoanCardClick = (index: number) => {
    if (data) {
      navigateToPostAuth(index, data);
    }
  };

  return <LoansListV2 data={data} isLoading={isLoading} isFetching={isFetching} onLoanCardClick={handleLoanCardClick} />;
}
